// Story 063 — cross-window state sync via BroadcastChannel.
//
// Mirrors the structural pattern of `lock/useLockBroadcastChannel.ts`:
// pure runtime + thin React hook + test seam for the channel factory.
// On Safari (partial / version-dependent BroadcastChannel support) the
// factory falls back to a `localStorage`-event polyfill — slower but
// functional (D-7 / AC12).
//
// Messages exchanged on `aero-lyse-sync-<da-id>`:
//
//   { type: 'selection',  activeFindingId: string | null }
//   { type: 'image',      activeImageId: string | null }
//   { type: 'filter',     selection, aiClassFilter }   // SB6-ANA-POP-04: the
//                                       // global 7-axis data-point selection
//                                       // (incl. `defectTypeIds`) PLUS the AI
//                                       // widget's committed `aiClassFilter`
//                                       // JSON. Both per-window Zustand stores
//                                       // are not shared, so a popped filter
//                                       // drives the main window only by
//                                       // broadcasting these. The AI
//                                       // `aiClassFilter` rides here (NOT in
//                                       // the persisted prefs / URL) so the
//                                       // non-popped case is byte-identical.
//   { type: 'requestFilter' }           // SB6-ANA-POP-04: child → parent on
//                                       // boot: "send me the current filter"
//                                       // (BroadcastChannel has no buffer).
//   { type: 'phaseChange', from, to }   // story 065 cross-window
//   { type: 'lockReleased' }            // story 048 AC10 cross-window
//   { type: 'closing',    popoutId }    // child → parent: window is closing
//   { type: 'closeRequest', popoutId }  // SB6-ANA-POP-01: parent → child:
//                                       // "please close yourself" (header
//                                       // toggle / Settings bring-back).
//   { type: 'requestSync' }             // SB6-ANA-POP-01: child → parent on
//                                       // boot: "send me the current image +
//                                       // selection + prefs" (BroadcastChannel
//                                       // has no buffer, so a child that
//                                       // subscribes after the parent's initial
//                                       // push must ask for state once ready).
//   { type: 'prefs', prefs }            // SB6-ANA-POP-01: parent → child: the
//                                       // user changed Settings (max-zoom, bbox
//                                       // colours…). The child has its own
//                                       // TanStack Query cache, so we forward
//                                       // the new LysePreferences for it to
//                                       // write into its cache. `prefs` is typed
//                                       // `unknown` here to keep this module
//                                       // free of the preferences schema.
//
// Propagation target: < 100 ms within the same machine (BroadcastChannel
// native) or < 200 ms (Safari polyfill).
import { useEffect, useRef } from "react";

export type SyncMessage =
  | { type: "selection"; activeFindingId: string | null }
  | { type: "image"; activeImageId: string | null }
  // SB6-ANA-POP-04 — the global data-point selection + the AI widget's
  // committed `aiClassFilter` JSON. `selection` is typed `unknown` to keep
  // this module free of the filter-store types (the filter-sync hook casts);
  // `aiClassFilter` is the wire JSON string or `null` (no AI narrowing).
  | { type: "filter"; selection: unknown; aiClassFilter: string | null }
  | { type: "requestFilter" }
  | { type: "phaseChange"; from: string | null; to: string | null }
  | { type: "lockReleased" }
  | { type: "closing"; popoutId: string }
  | { type: "closeRequest"; popoutId: string }
  | { type: "requestSync" }
  | { type: "prefs"; prefs: unknown }
  // SB6-ANA-POP-02 — workspace (which widgets live in which window + each
  // window's grid). `windows` is typed `unknown` to keep this module free of
  // the workspace store's types; the workspace-sync hook casts.
  | { type: "workspace"; version: number; windows: unknown }
  | { type: "requestWorkspace" };

export interface SyncChannel {
  readonly postMessage: (msg: SyncMessage) => void;
  readonly addListener: (fn: (msg: SyncMessage) => void) => () => void;
  readonly close: () => void;
}

export interface CreateSyncChannelArgs {
  dataAcquisitionId: string;
  /** Test seam — defaults to globalThis.BroadcastChannel. Returns null
   *  when BroadcastChannel is unavailable so the caller can opt into
   *  the localStorage-event polyfill. */
  channelFactory?: (name: string) => BroadcastChannel | null;
}

const POLYFILL_KEY_PREFIX = "aero.lyse.sync.";

export function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Match Safari but not Chrome/Edge/Brave which embed "Safari" too.
  return ua.includes("Safari") && !/Chrome|Chromium|Edg|OPR/.test(ua);
}

export function createSyncChannel(args: CreateSyncChannelArgs): SyncChannel {
  const name = "aero-lyse-sync-" + args.dataAcquisitionId;
  const factory =
    args.channelFactory ??
    ((n: string) => {
      if (typeof BroadcastChannel === "undefined") return null;
      try {
        return new BroadcastChannel(n);
      } catch {
        return null;
      }
    });
  const channel = factory(name);
  const listeners = new Set<(msg: SyncMessage) => void>();

  // Native BroadcastChannel path.
  if (channel) {
    const handler = (event: MessageEvent): void => {
      const data = event.data as SyncMessage | null;
      if (!data || typeof data !== "object") return;
      for (const fn of listeners) fn(data);
    };
    channel.addEventListener("message", handler);
    return {
      postMessage: (msg: SyncMessage): void => {
        try {
          channel.postMessage(msg);
        } catch {
          /* swallow */
        }
      },
      addListener: (fn): (() => void) => {
        listeners.add(fn);
        return () => {
          listeners.delete(fn);
        };
      },
      close: (): void => {
        channel.removeEventListener("message", handler);
        try {
          channel.close();
        } catch {
          /* swallow */
        }
        listeners.clear();
      },
    };
  }

  // Safari / no-BC polyfill path: bounce messages off localStorage.
  // Writers set a versioned key; readers listen to 'storage' events.
  const polyfillKey = POLYFILL_KEY_PREFIX + name;
  const onStorage = (event: Event): void => {
    const e = event as StorageEvent;
    if (e.key !== polyfillKey) return;
    if (!e.newValue) return;
    try {
      const data = JSON.parse(e.newValue) as { msg?: SyncMessage };
      if (!data.msg) return;
      for (const fn of listeners) fn(data.msg);
    } catch {
      /* swallow */
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return {
    postMessage: (msg: SyncMessage): void => {
      if (typeof window === "undefined") return;
      try {
        // Include nonce so identical messages still fire 'storage'.
        const nonce = String(Date.now()) + ":" + String(Math.random());
        window.localStorage.setItem(
          polyfillKey,
          JSON.stringify({ msg, nonce }),
        );
      } catch {
        /* swallow */
      }
    },
    addListener: (fn): (() => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    close: (): void => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", onStorage);
      }
      listeners.clear();
    },
  };
}

/** React hook wrapper — installs the channel for the lifetime of the
 *  component. Selection / image / filter syncing is done by the
 *  callers (they post on their own state changes; the listener fan-in
 *  is installed at `PopoutHost` and at `AnalysisFeature`). */
export function useSyncChannel(args: {
  dataAcquisitionId: string;
  enabled: boolean;
  onMessage: (msg: SyncMessage) => void;
  channelFactory?: (name: string) => BroadcastChannel | null;
}): React.RefObject<SyncChannel | null> {
  const ref = useRef<SyncChannel | null>(null);
  useEffect(() => {
    if (!args.enabled) return;
    const ch = createSyncChannel({
      dataAcquisitionId: args.dataAcquisitionId,
      ...(args.channelFactory ? { channelFactory: args.channelFactory } : {}),
    });
    ref.current = ch;
    const off = ch.addListener(args.onMessage);
    return () => {
      off();
      ch.close();
      ref.current = null;
    };
  }, [
    args.dataAcquisitionId,
    args.enabled,
    args.channelFactory,
    args.onMessage,
  ]);
  return ref;
}
